import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  WbSunny,
  FilterDrama,
  Cloud,
  WaterDrop,
  Grain,
  Thunderstorm,
  AcUnit,
  BlurOn,
  Opacity,
  Air,
  NavigateNext,
  Refresh,
} from '@mui/icons-material';
import { getWeatherForecast } from '../api/common';

export const getWeatherDetails = (code, size = 40) => {
  const iconProps = { fontSize: size };

  const mapping = {
    0: {
      description: 'Clear sky',
      icon: <WbSunny sx={{ color: 'gold', ...iconProps }} />,
    },
    1: {
      description: 'Mainly clear',
      icon: <FilterDrama sx={{ color: '#f5f5f5', ...iconProps }} />,
    },
    2: {
      description: 'Partly cloudy',
      icon: <Cloud sx={{ color: '#c0c0c0', ...iconProps }} />,
    },
    3: {
      description: 'Overcast Clouds',
      icon: <Cloud sx={{ color: '#808080', ...iconProps }} />,
    },
    45: {
      description: 'Fog',
      icon: <BlurOn sx={{ color: '#a9a9a9', ...iconProps }} />,
    },
    48: {
      description: 'Depositing rime fog',
      icon: <BlurOn sx={{ color: '#708090', ...iconProps }} />,
    },
    51: {
      description: 'Light drizzle',
      icon: <Grain sx={{ color: '#add8e6', ...iconProps }} />,
    },
    53: {
      description: 'Moderate drizzle',
      icon: <Grain sx={{ color: '#87cefa', ...iconProps }} />,
    },
    55: {
      description: 'Dense drizzle',
      icon: <Grain sx={{ color: '#4682b4', ...iconProps }} />,
    },
    56: {
      description: 'freezing drizzle',
      icon: <Grain sx={{ color: '#b0e0e6', ...iconProps }} />,
    },
    57: {
      description: 'freezing drizzle',
      icon: <Grain sx={{ color: '#5f9ea0', ...iconProps }} />,
    },
    61: {
      description: 'Slight rain',
      icon: <WaterDrop sx={{ color: '#87ceeb', ...iconProps }} />,
    },
    63: {
      description: 'Moderate rain',
      icon: <WaterDrop sx={{ color: '#4682b4', ...iconProps }} />,
    },
    65: {
      description: 'Heavy rain',
      icon: <WaterDrop sx={{ color: '#1e90ff', ...iconProps }} />,
    },
    66: {
      description: 'freezing rain',
      icon: <WaterDrop sx={{ color: '#00ced1', ...iconProps }} />,
    },
    67: {
      description: 'freezing rain',
      icon: <WaterDrop sx={{ color: '#20b2aa', ...iconProps }} />,
    },
    71: {
      description: 'Slight snow fall',
      icon: <AcUnit sx={{ color: '#e0f7fa', ...iconProps }} />,
    },
    73: {
      description: 'Moderate snow fall',
      icon: <AcUnit sx={{ color: '#b2ebf2', ...iconProps }} />,
    },
    75: {
      description: 'Heavy snow fall',
      icon: <AcUnit sx={{ color: '#80deea', ...iconProps }} />,
    },
    77: {
      description: 'Snow grains',
      icon: <AcUnit sx={{ color: '#b0e0e6', ...iconProps }} />,
    },
    80: {
      description: 'rain showers',
      icon: <Grain sx={{ color: '#add8e6', ...iconProps }} />,
    },
    81: {
      description: 'rain showers',
      icon: <Grain sx={{ color: '#87cefa', ...iconProps }} />,
    },
    82: {
      description: 'rain showers',
      icon: <Grain sx={{ color: '#4682b4', ...iconProps }} />,
    },
    85: {
      description: 'snow showers',
      icon: <AcUnit sx={{ color: '#e0ffff', ...iconProps }} />,
    },
    86: {
      description: 'snow showers',
      icon: <AcUnit sx={{ color: '#b0c4de', ...iconProps }} />,
    },
    95: {
      description: 'Thunderstorm',
      icon: <Thunderstorm sx={{ color: '#ffcc00', ...iconProps }} />,
    },
    96: {
      description: 'Thunderstorm',
      icon: <Thunderstorm sx={{ color: '#ff8c00', ...iconProps }} />,
    },
    99: {
      description: 'Thunderstorm',
      icon: <Thunderstorm sx={{ color: '#dc143c', ...iconProps }} />,
    },
  };

  return (
    mapping[code] || {
      description: 'Unknown weather',
      icon: <WbSunny sx={{ color: 'gray', ...iconProps }} />,
    }
  );
};

// === Subcomponents ===
const LocationDisplay = ({ loc }) =>
  loc ? (
    <Typography variant="h6" sx={{ color: 'white', fontWeight: 500 }}>
      {loc.district}, {loc.state}
    </Typography>
  ) : null;

const CurrentWeather = ({ data, showForecast, setShowForecast }) => {
  if (!data) return null;
  const { current_weather } = data;
  if (!current_weather) return null;

  return (
    <Grid container spacing={2} mt={1}>
      <Grid item xs={4}>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
          {current_weather.temperature}
        </Typography>
        <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
          Feels like {current_weather.apparent_temperature}
        </Typography>
      </Grid>
      <Grid item xs={4} textAlign="center">
        {getWeatherDetails(current_weather.weather_code).icon}
        <Typography
          variant="body2"
          sx={{ color: 'white', textTransform: 'capitalize' }}
        >
          {getWeatherDetails(current_weather.weather_code).description}
        </Typography>
      </Grid>
      <Grid item xs={4} mt={-2}>
        <Box display="flex" alignItems="center" mb={1}>
          <Opacity fontSize="small" sx={{ color: 'white', mr: 0.5 }} />
          <Typography variant="body2" sx={{ color: 'white' }}>
            {current_weather.humidity}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Air fontSize="small" sx={{ color: 'white', mr: 0.5 }} />
          <Typography variant="body2" sx={{ color: 'white' }}>
            {current_weather.wind_speed}
          </Typography>
        </Box>
        <Box>
          <Box display="flex" justifyContent="right" mt={2}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowForecast(!showForecast)}
              sx={{
                color: 'white',
                borderColor: 'white',
                '&:hover': { borderColor: 'white' },
              }}
              endIcon={
                <NavigateNext
                  sx={{
                    color: 'white',
                    transform: showForecast
                      ? 'rotate(-90deg)'
                      : 'rotate(90deg)',
                  }}
                />
              }
            >
              Forecast
            </Button>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

const Forecast = ({ data }) => {
  if (!data?.daily_forecast?.length) return null;

  return (
    <Box mt={2}>
      <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
        7-Day Forecast
      </Typography>
      <Box
        sx={{
          display: 'flex',
          overflowX: 'auto',
          pb: 1,
          gap: 1,
          '&::-webkit-scrollbar': { height: 8 },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.3)',
            borderRadius: 4,
          },
        }}
      >
        {data.daily_forecast.map((day, i) => (
          <Card
            key={i}
            sx={{
              minWidth: 100,
              background: 'rgba(255,255,255,0.08)',
              borderRadius: 2,
              backdropFilter: 'blur(10px)',
              flexShrink: 0,
            }}
          >
            <CardContent sx={{ px: 1.5, py: 2, textAlign: 'center' }}>
              <Typography
                variant="body2"
                sx={{ color: 'white', fontWeight: 'bold' }}
              >
                {new Date(day.date).toLocaleDateString('en-US', {
                  weekday: 'short',
                  day: 'numeric',
                })}
              </Typography>
              <Box my={1}>{getWeatherDetails(day.weather_code).icon}</Box>
              <Typography variant="body2" sx={{ color: 'white' }}>
                Max: <strong>{day.temperature_max}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: 'white', opacity: 0.7 }}>
                Min: {day.temperature_min}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

// === Main WeatherWidget ===
const WeatherWidget = () => {
  const [loc, setLoc] = useState(null);
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForecast, setShowForecast] = useState(false);

  const fetchWeatherData = async (latitude, longitude) => {
    try {
      const data = await getWeatherForecast(latitude, longitude);
      if (!data || !data.current_weather || !data.daily_forecast) {
        setWeather(null);
      } else {
        setWeather(data);
      }
    } catch (error) {
      console.error('Error fetching weather data:', error);
      setWeather(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const locData = sessionStorage.getItem('userLocation');
      if (locData) {
        const parsedLoc = JSON.parse(locData);
        if (parsedLoc.latitude && parsedLoc.longitude) {
          clearInterval(interval);
          setLoc(parsedLoc);
          fetchWeatherData(parsedLoc.latitude, parsedLoc.longitude);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Card sx={{ background: '#4370BC', borderRadius: 2, p: 2 }}>
        <Box display="flex" justifyContent="center" minHeight="180px">
          <CircularProgress sx={{ color: 'white' }} />
        </Box>
      </Card>
    );
  }

  if (!weather) {
    return (
      <Card
        sx={{
          background: 'linear-gradient(135deg, #5397E2, #4370BC)',
          borderRadius: 3,
          p: 3,
          boxShadow: 3,
          textAlign: 'center',
          color: 'white',
        }}
      >
        <Typography variant="body1" gutterBottom>
          Weather data not available.
        </Typography>
        <Button
          variant="outlined"
          onClick={() => {
            fetchWeatherData(loc.latitude, loc.longitude);
          }}
          startIcon={<Refresh />}
          sx={{
            mt: 1,
            borderColor: 'white',
            color: 'white',
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255,255,255,0.1)',
            },
          }}
        >
          Refresh
        </Button>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        background: 'linear-gradient(135deg, #5397E2, #4370BC)',
        borderRadius: 3,
        p: 2,
        boxShadow: 3,
        backdropFilter: 'blur(10px)',
        overflow: 'hidden',
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <LocationDisplay loc={loc} />
      </Box>
      <CurrentWeather
        data={weather}
        showForecast={showForecast}
        setShowForecast={setShowForecast}
      />
      {showForecast && <Forecast data={weather} />}
    </Card>
  );
};

export default WeatherWidget;
